import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Shield, Zap, Users, Clock, Gift, ChevronRight, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../components/common/Button';
import { Container } from '../components/layout/Container';
import { usePublicRaffles } from '../hooks/useRaffles';
import { Card } from '../components/common/Card';
import { CountdownTimer } from '../components/common/CountdownTimer';
import { RaffleCard } from '../components/raffle/RaffleCard';
import { Spinner } from '../components/common/Spinner';
import { formatCurrency } from '../utils/formatters';

export const Home: React.FC = () => {
  const { data: rafflesData, isLoading: rafflesLoading } = usePublicRaffles({ limit: 6 });

  // Find the raffle with the soonest upcoming draw
  const nextDrawRaffle = useMemo(() => {
    const raffles = rafflesData?.data?.raffles || [];
    return raffles
      .filter(r => r.nextDraw?.scheduledTime && new Date(r.nextDraw.scheduledTime) > new Date())
      .sort((a, b) => new Date(a.nextDraw!.scheduledTime).getTime() - new Date(b.nextDraw!.scheduledTime).getTime())[0] || null;
  }, [rafflesData]);

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <div className="relative min-h-[600px] flex items-center bg-gradient-to-br from-indigo-950 via-indigo-800 to-purple-900 text-white py-24">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-20 w-80 h-80 bg-indigo-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse [animation-delay:1s]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-400/10 rounded-full blur-3xl" />
          {/* Grid pattern overlay */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>

        <Container className="relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/10 rounded-full px-5 py-2.5 mb-8 text-sm font-medium">
              <Sparkles className="h-4 w-4 text-yellow-400" />
              <span className="text-indigo-100">Transparent split-the-pot raffles with real winners</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold mb-6 leading-[1.1] tracking-tight">
              Your Next Win is
              <span className="block bg-gradient-to-r from-yellow-300 via-amber-300 to-orange-300 bg-clip-text text-transparent">
                Just a Ticket Away
              </span>
            </h1>
            <p className="text-lg md:text-xl mb-10 text-indigo-200 max-w-2xl mx-auto leading-relaxed">
              RaffleBowl runs fair, transparent raffles where the pot is split among winners. 
              Pick a raffle, grab your tickets, and watch the draws live.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/raffles" className="group inline-flex items-center justify-center gap-2 font-semibold rounded-xl px-8 py-4 text-lg bg-white text-indigo-700 hover:bg-indigo-50 transition-all shadow-lg shadow-white/10 hover:shadow-xl hover:shadow-white/20">
                Browse Raffles
                <ArrowRight className="h-5 w-5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/register" className="inline-flex items-center justify-center font-semibold rounded-xl px-8 py-4 text-lg border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 transition-all backdrop-blur-sm">
                Create Account
              </Link>
            </div>
          </div>
        </Container>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 80V40C240 70 480 10 720 40C960 70 1200 10 1440 40V80H0Z" className="fill-white dark:fill-gray-950" />
          </svg>
        </div>
      </div>

      {/* Next Draw Countdown Banner */}
      {nextDrawRaffle && (
        <div className="bg-gradient-to-r from-indigo-900 via-indigo-800 to-purple-900 py-8 border-b border-indigo-700/50">
          <Container>
            <div className="text-center mb-4">
              <div className="inline-flex items-center gap-1.5 bg-white/10 rounded-full px-4 py-1.5 text-sm text-indigo-100 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Next Draw · {nextDrawRaffle.name}
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1 text-center md:text-left">
                <p className="text-xs uppercase tracking-wider text-emerald-300 font-semibold">Current Pot</p>
                <p className="text-4xl font-extrabold text-white mt-0.5">{formatCurrency(nextDrawRaffle.nextDraw?.sales ?? nextDrawRaffle.currentDrawSales ?? 0, nextDrawRaffle.currency)}</p>
                <p className="text-sm text-indigo-200 mt-1">{nextDrawRaffle.nextDraw?.ticketsSold ?? nextDrawRaffle.currentDrawTicketsSold ?? 0} tickets sold</p>
              </div>
              <div className="flex-1 flex justify-center">
                <CountdownTimer target={nextDrawRaffle.nextDraw!.scheduledTime} variant="hero" />
              </div>
              <div className="flex-1 flex justify-center md:justify-end">
                <Link to={`/raffles/${nextDrawRaffle.id}`} className="inline-flex items-center justify-center gap-2 font-bold rounded-xl px-6 py-3 text-sm bg-white text-indigo-700 hover:bg-indigo-50 shadow-lg transition-all whitespace-nowrap">
                  Get Tickets <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Container>
        </div>
      )}

      {/* How It Works */}
      <Container className="py-24">
        <div className="text-center mb-16">
          <p className="text-indigo-600 font-semibold text-sm uppercase tracking-wider mb-2">Getting Started</p>
          <h2 className="text-4xl font-bold text-gray-900 dark:text-gray-100">How It Works</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
          {/* Connector line (desktop only) */}
          <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-indigo-200 via-purple-200 to-emerald-200" />

          {[
            { step: '1', icon: Users, title: 'Sign Up', desc: 'Create your free account in seconds. No fees, no commitments.', color: 'indigo' },
            { step: '2', icon: Gift, title: 'Pick a Raffle', desc: 'Browse active raffles and buy tickets for the ones you like.', color: 'purple' },
            { step: '3', icon: Trophy, title: 'Win Prizes', desc: 'Winners are drawn automatically. Prizes go straight to your wallet.', color: 'emerald' },
          ].map((item) => {
            const colorMap: Record<string, { bg: string; iconBg: string; text: string; step: string }> = {
              indigo: { bg: 'bg-indigo-50 dark:bg-indigo-950 hover:bg-indigo-100/80', iconBg: 'bg-indigo-600', text: 'text-indigo-600', step: 'bg-indigo-600' },
              purple: { bg: 'bg-purple-50 dark:bg-purple-950 hover:bg-purple-100/80', iconBg: 'bg-purple-600', text: 'text-purple-600', step: 'bg-purple-600' },
              emerald: { bg: 'bg-emerald-50 dark:bg-emerald-950 hover:bg-emerald-100/80', iconBg: 'bg-emerald-600', text: 'text-emerald-600', step: 'bg-emerald-600' },
            };
            const c = colorMap[item.color];
            return (
              <div key={item.step} className={`${c.bg} rounded-2xl p-8 text-center relative transition-colors`}>
                <div className={`${c.step} text-white w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-5 text-sm font-bold relative z-10 shadow-lg`}>
                  {item.step}
                </div>
                <div className={`${c.iconBg} w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg`}>
                  <item.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">{item.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            );
          })}
        </div>
      </Container>

      {/* Featured Raffles Section */}
      <div className="relative bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950 py-24">
        <Container>
          <div className="flex items-center justify-between mb-12">
            <div>
              <p className="text-indigo-600 font-semibold text-sm uppercase tracking-wider mb-2">Don't Miss Out</p>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-gray-100">Featured Raffles</h2>
            </div>
            <Link to="/raffles" className="hidden sm:flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 font-semibold group">
              Browse All <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
          {rafflesLoading ? (
            <Spinner size="lg" />
          ) : rafflesData?.data?.raffles && rafflesData.data.raffles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...rafflesData.data.raffles].sort(() => Math.random() - 0.5).slice(0, 6).map((raffle) => (
                <RaffleCard key={raffle.id} raffle={raffle} />
              ))}
            </div>
          ) : (
            <Card className="text-center py-12">
              <Trophy className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">No active raffles right now</p>
              <p className="text-gray-400 dark:text-gray-500">Check back soon — new raffles are added regularly.</p>
            </Card>
          )}
          <div className="text-center mt-10 sm:hidden">
            <Link to="/raffles">
              <Button size="lg" variant="outline">Browse All Raffles</Button>
            </Link>
          </div>
        </Container>
      </div>

      {/* Why RaffleBowl */}
      <Container className="py-24">
        <div className="text-center mb-16">
          <p className="text-indigo-600 font-semibold text-sm uppercase tracking-wider mb-2">Why Choose Us</p>
          <h2 className="text-4xl font-bold text-gray-900 dark:text-gray-100">Built on Fairness</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Shield, title: '100% Transparent', desc: 'Every fee, prize split, and draw result is visible to all participants', gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50 dark:bg-emerald-950' },
            { icon: Zap, title: 'Instant Payouts', desc: 'Winnings are credited to your wallet immediately after each draw', gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-50 dark:bg-amber-950' },
            { icon: Clock, title: 'Regular Draws', desc: 'Daily, weekly, and monthly raffles so there\'s always something to play', gradient: 'from-violet-500 to-purple-600', bg: 'bg-violet-50 dark:bg-violet-950' },
            { icon: Users, title: 'Refer & Earn', desc: 'Share your referral link and earn commissions on every ticket your friends buy', gradient: 'from-sky-500 to-blue-600', bg: 'bg-sky-50 dark:bg-sky-950' },
          ].map((item) => (
            <div key={item.title} className={`${item.bg} rounded-2xl p-6 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group`}>
              <div className={`bg-gradient-to-br ${item.gradient} w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg group-hover:scale-110 transition-transform`}>
                <item.icon className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">{item.title}</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </Container>

      {/* CTA Section */}
      <div className="relative bg-gradient-to-br from-indigo-950 via-indigo-800 to-purple-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl" />
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>
        <Container className="relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 mb-6 text-sm">
            <Trophy className="h-4 w-4 text-yellow-400" />
            <span className="text-indigo-200">Thousands of winners and counting</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4">Ready to Try Your Luck?</h2>
          <p className="text-lg text-indigo-200 mb-10 max-w-xl mx-auto leading-relaxed">
            Join RaffleBowl today and get in on the next draw. It only takes a minute to sign up.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/raffles" className="group inline-flex items-center justify-center gap-2 font-semibold rounded-xl px-8 py-4 text-lg bg-white text-indigo-700 hover:bg-indigo-50 transition-all shadow-lg shadow-white/10">
              Browse Raffles
              <ArrowRight className="h-5 w-5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link to="/register" className="inline-flex items-center justify-center font-semibold rounded-xl px-8 py-4 text-lg border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 transition-all backdrop-blur-sm">
              Sign Up Free
            </Link>
          </div>
        </Container>
      </div>
    </div>
  );
};
